import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import axios from 'axios';
import { apiClient } from '../lib/api-client';

// 表單本身用 confirmPassword；送出前組出 API payload
const FormSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, '密碼至少 8 碼')
      .max(72, '密碼最長 72 碼')
      .regex(/[A-Za-z]/, '密碼須包含英文字母')
      .regex(/[0-9]/, '密碼須包含數字'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: '兩次輸入不一致',
  });
type FormInput = z.infer<typeof FormSchema>;

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormInput>({
    resolver: zodResolver(FormSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  if (!token) {
    return (
      <section className="max-w-sm mx-auto bg-white shadow-sm rounded-md p-6 mt-4">
        <h1 className="text-2xl font-semibold mb-4">重設密碼</h1>
        <p role="alert" className="text-sm text-red-600">
          連結無效（缺少 token）。請重新從忘記密碼流程取得連結。
        </p>
        <Link to="/forgot-password" className="text-blue-600 hover:underline text-sm mt-3 block">
          回到忘記密碼
        </Link>
      </section>
    );
  }

  const onSubmit = async (values: FormInput): Promise<void> => {
    setServerError(null);
    try {
      await apiClient.post('/auth/reset-password', {
        token,
        newPassword: values.newPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const msg = (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message;
        if (err.response?.status === 400) {
          setServerError(msg ?? '連結無效或已過期');
        } else if (err.response?.status === 429) {
          setServerError('嘗試次數過多，請稍後再試');
        } else {
          setServerError('重設失敗，請稍後再試');
        }
      } else {
        setServerError('重設失敗，請稍後再試');
      }
    }
  };

  return (
    <section className="max-w-sm mx-auto bg-white shadow-sm rounded-md p-6 mt-4">
      <h1 className="text-2xl font-semibold mb-4">重設密碼</h1>
      {success ? (
        <div role="status" className="text-sm text-green-700">
          密碼重設成功，即將跳轉登入頁…
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3">
          {serverError ? (
            <div role="alert" className="text-sm text-red-600">
              {serverError}
            </div>
          ) : null}
          <label className="grid gap-1">
            <span className="text-sm text-slate-700">新密碼</span>
            <input
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
              className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {errors.newPassword ? (
              <span role="alert" className="text-sm text-red-600">
                {errors.newPassword.message}
              </span>
            ) : null}
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-slate-700">確認新密碼</span>
            <input
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
              className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {errors.confirmPassword ? (
              <span role="alert" className="text-sm text-red-600">
                {errors.confirmPassword.message}
              </span>
            ) : null}
          </label>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? '重設中…' : '重設密碼'}
          </button>
        </form>
      )}
    </section>
  );
}
